import { isNotEqual } from 'vet-bones/utils';

import { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import {
  DiagramSnapshot,
  MappingLabelNode,
} from 'src/containers/ProjectDiagram/types/diagram';
import { ModelData } from 'src/containers/ProjectDiagram/types/model';
import { getPreviousClass } from 'src/containers/ProjectDiagram/utils/draft/draftClass';
import { getPreviousHierarchyConnection } from 'src/containers/ProjectDiagram/utils/draft/draftHierarchy';
import { getPreviousMapping } from 'src/containers/ProjectDiagram/utils/draft/draftMapping';
import {
  getIsColorIdModified,
  getIsDescriptionModified,
  getIsLabelModified,
} from 'src/containers/ProjectDiagram/utils/draft/draftModelConcept';
import {
  getPreviousRelationshipConnection,
  hasPreviousRelationship,
} from 'src/containers/ProjectDiagram/utils/draft/draftRelationship';
import { getPreviousResource } from 'src/containers/ProjectDiagram/utils/draft/draftResource';
import { isLinkLabel } from 'src/containers/ProjectDiagram/utils/graphObject';

const transformDiagramToSnapshot = (
  diagram: ExtendedDiagram
): DiagramSnapshot => ({
  id: '',
  model: {
    class: 'GraphLinksModel',
    linkDataArray: diagram.model.linkDataArray,
    linkKeyProperty: 'key',
    modelData: diagram.model.modelData,
    nodeDataArray: diagram.model.nodeDataArray,
  },
});

export const isMappingModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
): boolean => {
  const { draftData } = modelData;
  if (!draftData) {
    return false;
  }

  const objDiagram = obj.diagram;
  if (!objDiagram) {
    return false;
  }

  const objData =
    obj.part && isLinkLabel<MappingLabelNode>(obj.part)
      ? obj.part?.labeledLink?.data
      : obj.part?.data;
  if (!objData) {
    return false;
  }

  const diagram = transformDiagramToSnapshot(objDiagram as ExtendedDiagram);
  const { value: id } = objData;
  const prevMapping = getPreviousMapping(diagram, id);
  const isNewMapping = !prevMapping;
  if (isNewMapping) {
    return true;
  }

  return (
    // changed mapping target
    isNotEqual(prevMapping.to, objData.to) ||
    // updated mapping fields
    isNotEqual(prevMapping.fields, objData.fields) ||
    // updated mapping label field
    isNotEqual(prevMapping.label, objData.label) ||
    // updated mapping primary identifier field
    isNotEqual(prevMapping.primaryIdentifier, objData.primaryIdentifier) ||
    // updated mapping name
    isNotEqual(prevMapping.text, objData.text)
  );
};

export const isClassAttributesModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
): boolean => {
  const { draftData } = modelData;
  if (!draftData) {
    return false;
  }

  const objDiagram = obj.diagram;
  if (!objDiagram) {
    return false;
  }

  const objData = obj.part?.data;
  if (!objData) {
    return false;
  }

  const diagram = transformDiagramToSnapshot(objDiagram as ExtendedDiagram);
  const { key, value: iri } = objData;
  const isNewClass = !getPreviousClass(diagram, key);
  if (isNewClass) {
    return true;
  }

  const prevClassAttributes = new Set();
  const classAttributes = new Set();

  // eslint-disable-next-line guard-for-in, no-restricted-syntax
  for (const attributeIri in draftData.prevModelData.attributesByIri) {
    const attribute = draftData.prevModelData.attributesByIri[attributeIri];
    if (attribute.domains[iri]) {
      prevClassAttributes.add(attribute.value);
    }
  }

  // eslint-disable-next-line guard-for-in, no-restricted-syntax
  for (const attributeIri in modelData.attributesByIri) {
    const attribute = modelData.attributesByIri[attributeIri];
    if (attribute.domains[iri]) {
      classAttributes.add(attribute.value);
    }
  }

  return isNotEqual(prevClassAttributes, classAttributes);
};

export const isClassModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
): boolean => {
  const { draftData } = modelData;
  if (!draftData) {
    return false;
  }

  const objDiagram = obj.diagram;
  if (!objDiagram) {
    return false;
  }

  const objData = obj.part?.data;
  if (!objData) {
    return false;
  }

  const diagram = transformDiagramToSnapshot(objDiagram as ExtendedDiagram);
  const { key, value: iri } = objData;
  const isNewClass = !getPreviousClass(diagram, key);
  if (isNewClass) {
    return true;
  }

  const isColorIdModified = getIsColorIdModified({
    iri,
    isNewConcept: isNewClass,
    diagram,
  });

  const isDescriptionModified = getIsDescriptionModified({
    iri,
    isNewConcept: isNewClass,
    diagram,
  });

  const isLabelModified = getIsLabelModified({
    iri,
    isNewConcept: isNewClass,
    diagram,
  });

  return isColorIdModified || isDescriptionModified || isLabelModified;
};

export const isHierarchyModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
): boolean => {
  const { draftData } = modelData;
  if (!draftData) {
    return false;
  }

  const objDiagram = obj.diagram;
  if (!objDiagram) {
    return false;
  }

  const objData = obj.part?.data;
  if (!objData) {
    return false;
  }

  const diagram = transformDiagramToSnapshot(objDiagram as ExtendedDiagram);
  const isNewHierarchyConnection = !getPreviousHierarchyConnection(
    diagram,
    objData
  );

  return isNewHierarchyConnection;
};

export const isRelationshipModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
): boolean => {
  const { draftData } = modelData;
  if (!draftData) {
    return false;
  }

  const objDiagram = obj.diagram;
  if (!objDiagram) {
    return false;
  }

  const objData = obj.part?.data;
  if (!objData) {
    return false;
  }

  const diagram = transformDiagramToSnapshot(objDiagram as ExtendedDiagram);
  const isNewRelationshipConnection = !getPreviousRelationshipConnection(
    diagram,
    objData
  );
  if (isNewRelationshipConnection) {
    return true;
  }

  const { value: iri } = objData;
  const isNewRelationship = !hasPreviousRelationship(diagram, iri);
  if (isNewRelationship) {
    return true;
  }

  const isDescriptionModified = getIsDescriptionModified({
    iri,
    isNewConcept: isNewRelationship,
    diagram,
  });

  const isLabelModified = getIsLabelModified({
    iri,
    isNewConcept: isNewRelationship,
    diagram,
  });

  return isDescriptionModified || isLabelModified;
};

export const isResourceModified = (
  modelData: ModelData,
  obj: go.Shape | go.TextBlock
) => {
  const { draftData } = modelData;
  if (!draftData) {
    return false;
  }

  const objDiagram = obj.diagram;
  if (!objDiagram) {
    return false;
  }

  const objData = obj.part?.data;
  if (!objData) {
    return false;
  }

  const diagram = transformDiagramToSnapshot(objDiagram as ExtendedDiagram);
  const { key } = objData;
  const isNewResource = !getPreviousResource(diagram, key);

  return isNewResource;
};
